import { Box, Button, Stack, Textarea, Typography } from "@mui/joy";
import { TaskEditorActionComponent } from "pages/task-editor/utils/taskEditorUtils";
import { PreviewSessionApi } from "./usePreviewSession";
import { previewInputSheetSx } from "./previewStyles";
import { theme } from "styles/theme";
import { useState } from "react";

// v1 ButtonGroupCell.tsx 의 Stack sx 정확히 동일. vertical 고정이므로
// whiteSpace 는 "normal".
const previewOptionsStackSx = {
  width: "100%",
  whiteSpace: "normal",
  wordBreak: "keep-all",
  flexWrap: "wrap",
  gap: 1,
} as const;

interface PreviewActionProps {
  action: TaskEditorActionComponent;
  blockIndex: number; // 0-based
  blockSequence: number; // 1-based
  mode: "static" | "interactive";
  session: PreviewSessionApi;
  viewerMode: "patient" | "coach";
  totalBlocks: number;
}

function PreviewAction({
  action,
  blockIndex,
  blockSequence,
  mode,
  session,
  viewerMode,
}: PreviewActionProps) {
  const isInteractive = mode === "interactive";

  switch (action.type) {
    case "text_input":
      return (
        <Stack spacing="12px">
          {action.fields.map((field, fieldIdx) => (
            <TextInputField
              key={fieldIdx}
              field={field}
              blockIndex={blockIndex}
              fieldIndex={fieldIdx}
              isInteractive={isInteractive}
              session={session}
            />
          ))}
        </Stack>
      );

    case "button_default":
      return (
        <Stack direction="column" sx={previewOptionsStackSx}>
          {action.options.map((opt, optIdx) => {
            const selections = session.state.buttonSelections[blockIndex] ?? [];
            const isSelected = selections.includes(optIdx);
            return (
              <Box key={optIdx} sx={{ flexGrow: 1 }}>
                <Button
                  fullWidth
                  size="md"
                  variant={"Selection" as any}
                  color={isSelected ? "primary" : "secondary"}
                  onClick={
                    isInteractive
                      ? (e) => {
                          session.toggleButtonDefault(blockIndex, optIdx);
                          (e.currentTarget as HTMLElement).blur();
                        }
                      : undefined
                  }
                >
                  {opt.text || "(옵션 없음)"}
                </Button>
              </Box>
            );
          })}
        </Stack>
      );

    case "button_quiz":
      return (
        <QuizButtons
          action={action}
          blockIndex={blockIndex}
          isInteractive={isInteractive}
          viewerMode={viewerMode}
          session={session}
        />
      );

    case "button_branch":
      return (
        <Stack spacing="8px">
          <Stack direction="column" sx={previewOptionsStackSx}>
            {action.options.map((opt, optIdx) => {
              const selections =
                session.state.buttonSelections[blockIndex] ?? [];
              const isSelected = selections.includes(optIdx);
              return (
                <Box key={optIdx} sx={{ flexGrow: 1 }}>
                  <Button
                    fullWidth
                    size="md"
                    variant={"Selection" as any}
                    color={isSelected ? "primary" : "secondary"}
                    onClick={
                      isInteractive
                        ? (e) => {
                            session.chooseBranch(blockIndex, optIdx);
                            (e.currentTarget as HTMLElement).blur();
                          }
                        : undefined
                    }
                  >
                    {opt.text || "(옵션 없음)"}
                  </Button>
                </Box>
              );
            })}
          </Stack>
          {viewerMode === "coach" && (
            <Stack spacing="2px" sx={{ pl: "4px" }}>
              {action.options.map((opt, optIdx) => {
                const showFrom = opt.show_from ?? blockSequence + 1;
                return (
                  <Typography
                    key={optIdx}
                    variant="Caption"
                    sx={{ color: theme.vars.palette.Content.Weak }}
                  >
                    {`옵션 ${optIdx + 1} → 블록 ${showFrom}`}
                  </Typography>
                );
              })}
            </Stack>
          )}
        </Stack>
      );

    default: {
      const _never: never = action;
      void _never;
      return null;
    }
  }
}

// 텍스트 입력 필드 개별 컴포넌트
function TextInputField({
  field,
  blockIndex,
  fieldIndex,
  isInteractive,
  session,
}: {
  field: { label?: string; placeholder?: string; help_text?: string };
  blockIndex: number;
  fieldIndex: number;
  isInteractive: boolean;
  session: PreviewSessionApi;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const value = session.state.textInputs[blockIndex]?.[fieldIndex] ?? "";

  return (
    <Stack spacing="6px">
      {field.label && (
        <Typography
          variant="Caption"
          sx={{
            color: theme.vars.palette.Content.Highlight,
            fontWeight: 700,
          }}
        >
          {field.label}
        </Typography>
      )}
      <Box
        sx={{
          ...previewInputSheetSx,
          border: isFocused
            ? `1.4px solid ${theme.vars.palette.Border.Highlight}`
            : `1.4px solid ${theme.vars.palette.Border.Weak}`,
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {isInteractive ? (
          // v1 PlainTextarea 와 동일한 외형: 보더는 바깥 Box 가 담당하고 Joy
          // Textarea 는 투명·보더 0, 본문 16px/500, minRows=1 로 자동 높이 확장.
          <Textarea
            value={value}
            placeholder={field.placeholder ?? ""}
            onChange={(e) =>
              session.setTextInput(blockIndex, fieldIndex, e.target.value)
            }
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            minRows={1}
            sx={{
              width: "100%",
              minWidth: 0,
              padding: 0,
              backgroundColor: "transparent",
              borderWidth: 0,
              boxShadow: "none",
              "--joy-focus-thickness": "0px",
              "--variant-borderWidth": "0px",
              "--Textarea-gap": "0px",
              "& textarea": {
                fontSize: "16px",
                fontWeight: 500,
                lineHeight: 1.5,
                color: theme.vars.palette.Content.Strong,
              },
              "& textarea::placeholder": {
                color: theme.vars.palette.Content.SuperWeak,
              },
            }}
          />
        ) : (
          <Box
            sx={{
              fontSize: "16px",
              fontWeight: 500,
              lineHeight: 1.5,
              color: theme.vars.palette.Content.Weak,
              minHeight: "24px",
              pointerEvents: "none",
              width: "100%",
            }}
          >
            {field.placeholder && (
              <span style={{ opacity: 0.6 }}>{field.placeholder}</span>
            )}
          </Box>
        )}
      </Box>
      {field.help_text && (
        <Typography
          variant="Caption"
          sx={{ color: theme.vars.palette.Content.Weak }}
        >
          {field.help_text}
        </Typography>
      )}
    </Stack>
  );
}

// 퀴즈 버튼 — v1 Selection variant 기반.
// · 정답: 해당 옵션만 primary(Highlight) 표시 + advance.
// · 오답 시도: 클릭한 옵션을 Negative 보더/색으로 표시해 클릭이 먹었음을 알리고,
//   아래에 incorrect_message 안내를 띄운다. 정답을 맞출 때까지 다시 시도 가능.
function QuizButtons({
  action,
  blockIndex,
  isInteractive,
  viewerMode,
  session,
}: {
  action: Extract<TaskEditorActionComponent, { type: "button_quiz" }>;
  blockIndex: number;
  isInteractive: boolean;
  viewerMode: "patient" | "coach";
  session: PreviewSessionApi;
}) {
  const quizState = session.state.quizState[blockIndex];
  const isCorrect = quizState?.correct === true;
  const tried = quizState?.tried ?? [];
  // 한 번이라도 시도해서 정답을 못 맞춘 상태면 오답 안내 노출.
  // incorrect_message 가 비어있을 수 있으므로 fallback 사용.
  const showIncorrectAlert = isInteractive && !isCorrect && tried.length > 0;
  const incorrectMessage =
    action.incorrect_message?.trim() || "다시 한번 골라보세요.";

  return (
    <Stack spacing="8px">
      <Stack direction="column" sx={previewOptionsStackSx}>
        {action.options.map((opt, optIdx) => {
          // v1 ButtonGroupCell 패턴 그대로 — 마지막 클릭한 옵션이 primary,
          // 나머지는 secondary. 정/오답 보더 시각 구분 없음. 오답 피드백은
          // 아래 alert 텍스트로만 전달.
          const lastTried = tried[tried.length - 1];
          const isSelected = lastTried === optIdx;
          return (
            <Box key={optIdx} sx={{ flexGrow: 1 }}>
              <Button
                fullWidth
                size="md"
                variant={"Selection" as any}
                color={isSelected ? "primary" : "secondary"}
                onClick={
                  isInteractive
                    ? (e) => {
                        session.attemptQuiz(blockIndex, optIdx);
                        (e.currentTarget as HTMLElement).blur();
                      }
                    : undefined
                }
              >
                {opt.text || "(옵션 없음)"}
              </Button>
            </Box>
          );
        })}
      </Stack>
      {showIncorrectAlert && (
        <Box
          sx={{
            pl: "4px",
            fontSize: "13px",
            lineHeight: 1.4,
            // Content.Negative 는 #FFFFFF (부정 버튼 위의 흰 텍스트용) — 흰
            // Sheet 위에 흰 글자라 안 보임. 실제 빨강은 HighlightWithAlert.
            color: theme.vars.palette.Content.HighlightWithAlert,
            fontWeight: 600,
          }}
        >
          {incorrectMessage}
        </Box>
      )}
      {/* coach 정적 모드 진단 정보: 정답 표시 + 오답 메시지 */}
      {viewerMode === "coach" && !isInteractive && (
        <Stack spacing="2px" sx={{ pl: "4px" }}>
          {action.options.map((opt, optIdx) => {
            if (opt.is_correct !== true) return null;
            return (
              <Typography
                key={optIdx}
                variant="Caption"
                sx={{
                  color: theme.vars.palette.Content.Highlight,
                  fontWeight: 700,
                }}
              >
                {`옵션 ${optIdx + 1}: (정답)`}
              </Typography>
            );
          })}
          {action.incorrect_message && (
            <Typography
              variant="Caption"
              sx={{ color: theme.vars.palette.Content.Weak }}
            >
              {`오답 시: ${action.incorrect_message}`}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default PreviewAction;
